import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import PageHeader from '../../components/common/PageHeader';
import { Link } from 'react-router-dom';
import { FaBitcoin, FaChartBar, FaShieldAlt, FaGlobeAmericas } from 'react-icons/fa';

const BitcoinTradingPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbs = [
    { label: 'Fund', path: '/fund' },
    { label: 'Blockchain Fund', path: '/fund/blockchain' },
    { label: 'Bitcoin Trading Fund' }
  ];

  const strategies = [
    {
      title: "Directional Trading",
      description: "Capitalizing on medium to long-term Bitcoin price trends through technical and fundamental analysis."
    },
    {
      title: "Volatility Arbitrage",
      description: "Profiting from Bitcoin's price volatility through advanced options strategies and derivatives trading."
    },
    {
      title: "Futures Trading",
      description: "Strategic positioning in Bitcoin futures markets to capture price movements with leverage."
    },
    {
      title: "Cash & Carry",
      description: "Exploiting price differences between spot and futures markets through basis trading strategies."
    }
  ];

  const features = [
    {
      icon: <FaBitcoin className="w-8 h-8 text-secondary" />,
      title: "Bitcoin-Focused",
      description: "100% dedicated to Bitcoin trading strategies without dilution from other cryptocurrencies."
    },
    {
      icon: <FaChartBar className="w-8 h-8 text-secondary" />,
      title: "Quantitative Approach",
      description: "Data-driven trading algorithms and models that eliminate emotional decision-making."
    },
    {
      icon: <FaShieldAlt className="w-8 h-8 text-secondary" />,
      title: "Risk Management",
      description: "Robust risk controls including position limits, stop-loss mechanisms, and volatility filters."
    },
    {
      icon: <FaGlobeAmericas className="w-8 h-8 text-secondary" />,
      title: "Global Market Access",
      description: "Trading across multiple exchanges and markets to ensure optimal execution and liquidity."
    }
  ];

  return (
    <>
      <PageHeader 
        title="Bitcoin Trading Fund" 
        breadcrumbs={breadcrumbs}
      />
      
      <section className="py-8 md:py-24 bg-background">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary">
                Institutional-Grade Bitcoin Trading
              </h2>
              <p className="text-lg text-gray-700">
                Our Bitcoin Trading Fund provides sophisticated investors with actively managed exposure to Bitcoin through a diversified set of trading strategies designed to deliver strong risk-adjusted returns regardless of market conditions.
              </p>
              <p className="text-lg text-gray-700">
                Unlike passive Bitcoin holdings, our fund employs advanced trading techniques, proprietary algorithms, and professional risk management to capitalize on Bitcoin's volatility while mitigating downside risk.
              </p>
              <div className="pt-4">
                <Link 
                  to="/contact-us" 
                  className="px-8 py-3 bg-secondary hover:bg-secondary-dark text-white font-semibold rounded-lg transition-colors inline-block"
                >
                  Request Fund Information
                </Link>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              <img 
                src="/assets/hand-trading-forex-online-tablet-with-business-paper-coffee_1048944-27887900.jpg_x2g93v.jpg" 
                alt="Bitcoin Trading Operations" 
                className="rounded-xl shadow-lg w-full h-auto object-cover"
              />
            </motion.div>
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h3 className="text-2xl md:text-3xl font-heading font-bold text-primary mb-4">
              Trading Strategies
            </h3>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our fund deploys multiple trading strategies that work synergistically to capture value across different market conditions.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-20">
            {strategies.map((strategy, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-8 shadow-md border-l-4 border-secondary"
              >
                <h4 className="text-xl font-heading font-semibold mb-3">{strategy.title}</h4>
                <p className="text-gray-600">{strategy.description}</p>
              </motion.div>
            ))}
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h3 className="text-2xl md:text-3xl font-heading font-bold text-primary mb-8">
              Fund Features
            </h3>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-8 shadow-md hover:shadow-lg transition-all duration-300"
              >
                <div className="mb-4">{feature.icon}</div>
                <h4 className="text-xl font-heading font-semibold mb-3">{feature.title}</h4>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
          
          <div className="mt-16 bg-primary text-white rounded-xl p-10 shadow-lg">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-4xl font-bold text-secondary mb-2">$80M+</div>
                <div className="text-gray-300">Assets Under Management</div>
              </motion.div>
              
              <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-4xl font-bold text-secondary mb-2">36.7%</div>
                <div className="text-gray-300">Historical Annual Return</div>
              </motion.div>
              
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-4xl font-bold text-secondary mb-2">0.82</div>
                <div className="text-gray-300">Sharpe Ratio</div>
            </motion.div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default BitcoinTradingPage;
